/**
 * AuthModal - Clerk Authentication Modal
 * Uses Clerk's SignIn and SignUp components for authentication
 */
import { SignIn, SignUp } from '@clerk/clerk-react'
import { useState, useEffect } from 'react'

interface AuthModalProps {
  isOpen: boolean
  onClose: () => void
  initialMode?: 'login' | 'signup'
}

export default function AuthModal({ isOpen, onClose, initialMode = 'login' }: AuthModalProps) {
  const [mode, setMode] = useState<'login' | 'signup'>(initialMode)

  useEffect(() => {
    setMode(initialMode)
  }, [initialMode])

  if (!isOpen) return null

  return (
    <div 
      className="modal-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        backdropFilter: 'blur(8px)',
      }}
      onClick={onClose}
    >
      <div 
        className="modal-content"
        style={{
          background: 'var(--card-bg)',
          borderRadius: '16px',
          padding: '24px',
          maxWidth: '480px',
          width: '95%',
          maxHeight: '90vh',
          overflow: 'auto',
          border: '1px solid var(--border-color)',
          position: 'relative',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button 
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'transparent',
            border: 'none',
            color: 'var(--text-muted)',
            fontSize: '1.5rem',
            cursor: 'pointer',
            padding: '4px',
            zIndex: 10,
            lineHeight: 1,
          }}
        >
          ×
        </button>

        {/* Clerk Auth Components */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          paddingTop: '16px',
        }}>
          {mode === 'login' ? (
            <SignIn 
              appearance={{
                elements: {
                  rootBox: { width: '100%' },
                  card: { 
                    background: 'transparent',
                    boxShadow: 'none',
                    border: 'none',
                  },
                  headerTitle: {
                    background: 'linear-gradient(135deg, #ff3366, #ff6b35)',
                    WebkitBackgroundClip: 'text',
                    backgroundClip: 'text',
                    color: 'transparent',
                  },
                  formButtonPrimary: {
                    background: 'linear-gradient(135deg, #ff3366, #ff6b35)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #ff4477, #ff7c46)',
                    },
                  },
                  footerActionLink: {
                    color: '#ff3366',
                  },
                },
                variables: {
                  colorPrimary: '#ff3366',
                  colorBackground: 'var(--card-bg)',
                  colorText: 'var(--text-primary)',
                  colorTextSecondary: 'var(--text-muted)',
                  colorInputBackground: 'rgba(255, 255, 255, 0.05)',
                  colorInputText: 'var(--text-primary)',
                  borderRadius: '8px',
                },
              }}
              fallbackRedirectUrl="/"
              signUpFallbackRedirectUrl="/signup"
            />
          ) : (
            <SignUp 
              appearance={{
                elements: {
                  rootBox: { width: '100%' },
                  card: { 
                    background: 'transparent',
                    boxShadow: 'none',
                    border: 'none',
                  },
                  headerTitle: {
                    background: 'linear-gradient(135deg, #ff3366, #ff6b35)',
                    WebkitBackgroundClip: 'text',
                    backgroundClip: 'text',
                    color: 'transparent',
                  },
                  formButtonPrimary: {
                    background: 'linear-gradient(135deg, #ff3366, #ff6b35)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #ff4477, #ff7c46)',
                    },
                  },
                  footerActionLink: {
                    color: '#ff3366',
                  },
                },
                variables: {
                  colorPrimary: '#ff3366',
                  colorBackground: 'var(--card-bg)',
                  colorText: 'var(--text-primary)',
                  colorTextSecondary: 'var(--text-muted)',
                  colorInputBackground: 'rgba(255, 255, 255, 0.05)',
                  colorInputText: 'var(--text-primary)',
                  borderRadius: '8px',
                },
              }}
              fallbackRedirectUrl="/"
              signInFallbackRedirectUrl="/login"
            />
          )}

          {/* Mode Switch */}
          <div style={{ marginTop: '16px', textAlign: 'center' }}>
            <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
              {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
            </span>
            <button
              onClick={() => setMode(mode === 'login' ? 'signup' : 'login')}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--accent-primary)',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '600',
              }}
            >
              {mode === 'login' ? 'Sign Up' : 'Log In'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
